import { keyBy, pickBy } from 'lodash-es'
import type {
    MemberRoom,
    MembersRecent,
    MemberTag,
    MemberWebcam,
    TaggedMember,
} from '../../types'
import { MEMBER_ROOM_VIDEO } from './memberRooms'
import {
    CHANNEL_ID_START,
    FCUCR_BASICS,
    FCUCR_FRIENDS,
    FCUCR_MODELS,
    FCUCR_PREMIUMS,
} from './mfcConstants'

const webcam = (
    id: number,
    name: string,
    age: number | null,
    gender: string,
    topic: string,
    color: string,
): MemberWebcam => ({
    id,
    name,
    age,
    gender,
    topic,
    about: topic,
    color,
})

export const MOCK_WEBCAMS: Array<MemberWebcam> = [
    webcam(
        7100001,
        'NightOwlDan',
        31,
        'Male',
        'Late shift, coffee, bad jokes',
        '1565c0',
    ),
    webcam(
        7100002,
        'LunaSketches',
        26,
        'Female',
        'Drawing live, ask for a doodle',
        'ad1457',
    ),
    webcam(7100003, 'Brix_88', 29, '', 'Just hanging out', '455a64'),
    webcam(
        7100004,
        'CamCaddie',
        30,
        'Male',
        'Golf on TV, come talk shop',
        '33691e',
    ),
    webcam(
        7100005,
        'JaxOnline',
        23,
        'Male',
        'Ranked matches all night',
        '6a1b9a',
    ),
    webcam(
        7100006,
        'OldSchoolRay',
        52,
        'Male',
        'Blues guitar, requests welcome',
        '37474f',
    ),
    webcam(
        7100007,
        'PixelPete',
        41,
        'Male',
        'Retro consoles on the shelf behind me',
        '006064',
    ),
    webcam(
        7100008,
        'quietcoder',
        34,
        'Male',
        'Coding a game, say hi',
        '2e7d32',
    ),
    webcam(
        7100009,
        'RoseQuartzy',
        27,
        'Female',
        'Studying, keep me company',
        '8e24aa',
    ),
    webcam(
        7100010,
        'SunnyMarlo',
        38,
        'Female',
        'Cooking dinner, recipe in chat',
        'bf360c',
    ),
    webcam(
        7100011,
        'TeaAndTrains',
        45,
        'Male',
        'Model railway on the desk tonight',
        '6d4c41',
    ),
    webcam(
        7100012,
        'VelvetFox',
        33,
        'TS/TV/TG',
        'Music requests welcome',
        'c62828',
    ),
]

export const MOCK_MY_WEBCAM: MemberWebcam = webcam(
    7199999,
    'You',
    null,
    '',
    '',
    '',
)

export const MOCK_FRIEND_IDS: Record<number, boolean> = {
    7100001: true,
    7100002: true,
}

const room = (
    ownerId: number,
    ownerName: string,
    name: string,
    description: string,
    color: string,
    permissions: number,
    video: number,
    visitors: number,
    since: number,
): MemberRoom => ({
    id: ownerId + CHANNEL_ID_START,
    ownerId,
    ownerName,
    name,
    description,
    color,
    font: 0,
    options: 0,
    background: 0,
    permissions,
    video,
    visitors,
    since,
})

const MOCK_ROOMS: Array<MemberRoom> = [
    room(
        7100001,
        'NightOwlDan',
        'Late Night Lounge',
        'Chill chat for night owls. Be nice, stay a while.',
        '1565c0',
        FCUCR_PREMIUMS | FCUCR_BASICS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        24,
        1792630000,
    ),
    room(
        7100002,
        'LunaSketches',
        'Sketch Club',
        'Drawing prompts every hour. Bring a pencil.',
        'ad1457',
        FCUCR_FRIENDS | FCUCR_PREMIUMS,
        MEMBER_ROOM_VIDEO.LOUNGE,
        17,
        1792633000,
    ),
    room(
        7100007,
        'PixelPete',
        'Retro Gaming Talk',
        'Consoles, carts and high scores from the 80s and 90s.',
        '006064',
        FCUCR_PREMIUMS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        12,
        1792636000,
    ),
    room(
        7100010,
        'SunnyMarlo',
        'Cooking With Marlo',
        'Making risotto tonight, recipe in the topic.',
        'bf360c',
        FCUCR_MODELS | FCUCR_PREMIUMS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        9,
        1792638000,
    ),
    room(
        7100006,
        'OldSchoolRay',
        'Blues Jam',
        'Twelve bars and a cheap guitar. Requests welcome.',
        '37474f',
        FCUCR_PREMIUMS | FCUCR_BASICS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        7,
        1792639000,
    ),
    room(
        7100011,
        'TeaAndTrains',
        'Rail Fans',
        'N gauge layout running live on my desk.',
        '6d4c41',
        FCUCR_PREMIUMS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        5,
        1792640000,
    ),
    room(
        7100009,
        'RoseQuartzy',
        'Study Hall',
        'Quiet room, pomodoro timer in the topic.',
        '8e24aa',
        FCUCR_FRIENDS | FCUCR_PREMIUMS | FCUCR_BASICS,
        MEMBER_ROOM_VIDEO.LOUNGE,
        4,
        1792641000,
    ),
    room(
        7100005,
        'JaxOnline',
        'Ranked Grind',
        'Climbing ladder all night, come heckle.',
        '6a1b9a',
        FCUCR_PREMIUMS,
        MEMBER_ROOM_VIDEO.OWNER_WEBCAM,
        3,
        1792642000,
    ),
]

export const MOCK_ROOMS_BY_ID: Record<number, MemberRoom> = keyBy(
    MOCK_ROOMS,
    'id',
)

export const mockRoomsWithoutOwners = (ownerIds: Array<number | null>) =>
    pickBy(MOCK_ROOMS_BY_ID, (room) => !ownerIds.includes(room.ownerId))

export const MOCK_RECENT: MembersRecent = {
    rooms: [
        {
            ownerId: 7100007,
            ownerName: 'PixelPete',
            name: 'Retro Gaming Talk',
            at: 1792639000000,
        },
        {
            ownerId: 7100099,
            ownerName: 'MidnightMo',
            name: 'Midnight Movie Club',
            at: 1792620000000,
        },
    ],
    webcams: [
        { id: 7100002, name: 'LunaSketches', at: 1792638000000 },
        { id: 7100098, name: 'OffAirOllie', at: 1792610000000 },
    ],
}

export const MOCK_TAGS: Array<MemberTag> = [
    { tag: 'gaming', count: 148 },
    { tag: 'music', count: 131 },
    { tag: 'movies', count: 97 },
    { tag: 'travel', count: 88 },
    { tag: 'cooking', count: 76 },
    { tag: 'fitness', count: 64 },
    { tag: 'art', count: 52 },
    { tag: 'anime', count: 49 },
    { tag: 'photography', count: 41 },
    { tag: 'cars', count: 37 },
    { tag: 'sports', count: 35 },
    { tag: 'books', count: 28 },
]

export const MOCK_TAGGED: Array<TaggedMember> = [
    {
        id: 7100012,
        name: 'VelvetFox',
        color: 'c62828',
        tags: ['music', 'vinyl', 'cats'],
    },
    {
        id: 7100006,
        name: 'OldSchoolRay',
        color: '37474f',
        tags: ['music', 'guitar', 'blues'],
    },
    {
        id: 7100001,
        name: 'NightOwlDan',
        color: '1565c0',
        tags: ['music', 'coffee', 'movies'],
    },
    {
        id: 7100002,
        name: 'LunaSketches',
        color: 'ad1457',
        tags: ['art', 'music', 'anime'],
    },
    {
        id: 7100013,
        name: 'HarborLights',
        color: '01579b',
        tags: ['music', 'sailing', 'travel'],
    },
    {
        id: 7100014,
        name: 'Kestrel_K',
        color: '5d4037',
        tags: ['music', 'birds', 'photography'],
    },
]

export const MOCK_MY_TAGS = ['gaming', 'music', 'coffee', 'night shift']
